import {assertAndContinue} from 'Asserts';
import {
  WarehouseColumnData,
  WarehouseModelData,
  WarehouseTableData,
} from 'insights/warehouse/warehouseModelsDataLayout';
import {pointToString} from 'util/geometry/GeometryConstants';

import {
  BASE_DIAGRAM_LAYOUT,
  DWD_FOCUS_TABLE_LOCATION,
  DWD_HORIZONTAL_GUTTER_SIZE,
  DWD_MAX_COLLAPSED_COLUMNS_SHOWN,
  DWD_MAX_COLLAPSED_TABLE_HEIGHT,
  DWD_MAXIMUM_NUMBER_TABLES_TO_ADD,
  DWD_TABLE_HEADER_HEIGHT,
  DWD_TABLE_HEIGHT_PER_ROW,
  DWD_TABLE_WIDTH,
  DWD_VERTICAL_GUTTER_SIZE,
  dwdHeightOfTableWithNColumns,
} from './diagramLayoutConstants';
import {DiagramGridCell, DiagramLayout, DiagramPixelPoint, VerticalSide} from './diagramLayoutTypes';

/**
 * Return integer maximum pixel height of the expanded view of a table in the grid (all columns shown).
 *
 * Excludes horizontal gutter at the bottom of the table.
 */
export function expandedTableHeight(tableName: string, models: WarehouseModelData): number {
  return dwdHeightOfTableWithNColumns(models.tables.get(tableName)!.columns.length);
}

/**
 * Convert the input grid cell coordinate to the pixel coordinate of the top-left of the input grid cell
 * relative to the top-left of grid cell (0, 0).
 *
 * For example, cellToTopLeftPixel(..., {x: 3, y: -1}) might return:
 * {x: 300, y: -200}
 */
export function cellToTopLeftPixel(cell: DiagramGridCell): DiagramPixelPoint {
  return {
    x: cell.x * (DWD_TABLE_WIDTH + DWD_VERTICAL_GUTTER_SIZE),
    y: cell.y * (DWD_MAX_COLLAPSED_TABLE_HEIGHT + DWD_HORIZONTAL_GUTTER_SIZE),
  };
}

/**
 * Convert from a pixel coordinate relative to the top-left of grid cell (0, 0) to a grid cell coordinate.
 *
 * Considers the grid cell to include the table, the horizontal gutter below the table,
 * and the vertical gutter to the right of the table.
 *
 * For example, pixelToCell(..., {xTranslation: 200, yTranslation: 322}) might return
 * {x: 1, y: 2}
 */
export function pixelToCell(pixel: DiagramPixelPoint): DiagramGridCell {
  return {
    x: Math.floor(pixel.x / (DWD_TABLE_WIDTH + DWD_VERTICAL_GUTTER_SIZE)),
    y: Math.floor(pixel.y / (DWD_MAX_COLLAPSED_TABLE_HEIGHT + DWD_HORIZONTAL_GUTTER_SIZE)),
  };
}

export function cellIsObscured(
  layout: DiagramLayout,
  cell: DiagramGridCell,
  models: WarehouseModelData
): boolean {
  for (const candidateTableName of layout.verticallyExpandedTables) {
    const candidateCell = layout.tableNameToCell.get(candidateTableName)!;
    if (!(cell.x === candidateCell.x && cell.y > candidateCell.y)) {
      // Cell can only be obscured by candidate cells in the same column & above it
      continue;
    }
    const candidateTopLeftTranslation = cellToTopLeftPixel(candidateCell);
    const candidateLowestCell = pixelToCell({
      x: candidateTopLeftTranslation.x,
      y: candidateTopLeftTranslation.y + expandedTableHeight(candidateTableName, models),
    });

    if (candidateLowestCell.y >= cell.y) {
      return true;
    }
  }
  return false;
}

export function columnIsShown(
  layout: DiagramLayout,
  column: WarehouseColumnData,
  models: WarehouseModelData
): boolean {
  return (
    layout.tableNameToCell.has(column.table.name) &&
    column.index <
      (layout.verticallyExpandedTables.has(column.table.name)
        ? column.table.columns.length
        : DWD_MAX_COLLAPSED_COLUMNS_SHOWN) &&
    !cellIsObscured(layout, layout.tableNameToCell.get(column.table.name)!, models)
  );
}

/**
 * Returns the pixel distance from the top of a table to the vertical center of the input column.
 */
export function columnVerticalOffset(column: WarehouseColumnData): number {
  return DWD_TABLE_HEADER_HEIGHT + DWD_TABLE_HEIGHT_PER_ROW * column.index + DWD_TABLE_HEIGHT_PER_ROW / 2;
}

/**
 * Returns the pixel coordinate in the vertical-center and horizontal-`side` of the input column.
 */
export function columnToPixel(
  layout: DiagramLayout,
  column: WarehouseColumnData,
  side: VerticalSide
): DiagramPixelPoint {
  const topLeftPixel = cellToTopLeftPixel(layout.tableNameToCell.get(column.table.name)!);
  return {
    x: topLeftPixel.x + (side === VerticalSide.LEFT ? 0 : DWD_TABLE_WIDTH),
    y: topLeftPixel.y + columnVerticalOffset(column),
  };
}

/**
 * Perform a breadth-first search to find the nearest grid cell locations to place new tables.
 *
 * @param originCell: grid cell to search near.
 * @param numberToPlace: number of empty grid cells to find.
 * @returns array of `numberToPlace` DiagramGridCells which are both unoccupied in the input layout as well
 *     as as close to `originCell` as possible. The cells are allocated in right/top/left/bottom order.
 *
 * If originCell is unoccupied, it will be the first member in the returned array.
 */
export function getNewTableCellsNear(
  layout: DiagramLayout,
  originCell: DiagramGridCell,
  numberToPlace: number
): Array<DiagramGridCell> {
  if (numberToPlace <= 0) {
    return [];
  }
  const result: Array<DiagramGridCell> = [];

  const visited = new Set<string>(); // string version of LayoutGridCell
  const toProcess: Array<DiagramGridCell> = [originCell];
  while (toProcess.length > 0) {
    const candidate = toProcess.shift() as DiagramGridCell;
    if (visited.has(pointToString(candidate))) {
      continue;
    }

    visited.add(pointToString(candidate));
    if (!layout.cellToTableName.has(candidate)) {
      result.push(candidate);
      if (result.length === numberToPlace) {
        return result;
      }
    }
    const {x, y} = candidate;
    toProcess.push(
      {x: x + 1, y}, // right neighbor
      {x, y: y - 1}, // top neighbor
      {x: x - 1, y}, // left neighbor
      {x, y: y + 1} // bottom neighbor
    );
  }
  // This code should be impossible to reach given the grid is unbounded, but just in case
  // that assumption changes or there is a bug in the code, return all the points successfully allocated
  // after raising an error.
  assertAndContinue(false, 'Could not find enough empty diagram positions');
  return result;
}

/**
 * Returns set of table names that either have relationships to the input table, or that the input table has
 * relationships to, and that are not yet placed in the diagram.
 *
 * The input table is never included in the output.
 */
export function getUnpopulatedRelatedTables(layout: DiagramLayout, table: WarehouseTableData): Set<string> {
  const allRelatedTables: Array<string> = table.columns.flatMap((column) =>
    column.relationships.flatMap((relationship) => [
      relationship.source.table.name,
      relationship.target.table.name,
    ])
  );
  allRelatedTables.push(table.name);
  return new Set(
    allRelatedTables.filter((tableName) => !layout.tableNameToCell.has(tableName) && tableName !== table.name)
  );
}

export function initializeDiagramLayout(baseTable: WarehouseTableData): DiagramLayout {
  const layoutWithFocus: DiagramLayout = {
    ...BASE_DIAGRAM_LAYOUT,
    tableNameToCell: BASE_DIAGRAM_LAYOUT.tableNameToCell.set(baseTable.name, DWD_FOCUS_TABLE_LOCATION),
    cellToTableName: BASE_DIAGRAM_LAYOUT.cellToTableName.set(DWD_FOCUS_TABLE_LOCATION, baseTable.name),
  };
  const unpopulatedRelatedTables = getUnpopulatedRelatedTables(layoutWithFocus, baseTable);
  const newTableCells = getNewTableCellsNear(
    layoutWithFocus,
    DWD_FOCUS_TABLE_LOCATION,
    // Cap at max # - 1 because focus table is already in the layout at this point.
    // This keeps the number of tables initially in the diagram at DWD_MAXIMUM_NUMBER_TABLES_TO_ADD
    // rather than DWD_MAXIMUM_NUMBER_TABLES_TO_ADD + 1.
    Math.min(unpopulatedRelatedTables.size, DWD_MAXIMUM_NUMBER_TABLES_TO_ADD - 1)
  );
  const tablesToAdd = Array.from(unpopulatedRelatedTables).slice(0, DWD_MAXIMUM_NUMBER_TABLES_TO_ADD - 1);

  return addTablesToLayout(layoutWithFocus, tablesToAdd, newTableCells);
}

/**
 * Return a new DiagramLayout with the input tableNames placed at the input positions.
 *
 * Will overwrite any other tables in the input positions.
 */
export function addTablesToLayout(
  diagramLayout: DiagramLayout,
  tableNames: Array<string>,
  cells: Array<DiagramGridCell>
): DiagramLayout {
  // eslint-disable-next-line benchlint/no-spread-reduce
  return tableNames.reduce(
    (newDiagramLayout: DiagramLayout, tableName: string, i: number) => ({
      ...newDiagramLayout,
      tableNameToCell: newDiagramLayout.tableNameToCell.set(tableName, cells[i]),
      cellToTableName: newDiagramLayout.cellToTableName.set(cells[i], tableName),
    }),
    diagramLayout
  );
}
